<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <header v-if="isAdmin" class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex justify-between items-center max-w-7xl mx-auto">
        <h1 class="text-xl font-semibold text-indigo-300">จัดการอาชญากร</h1>
        <button 
          @click="toggleModal = 'AddCriminalModal'" 
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="สร้างอาชญากรใหม่"
        >
          <i class="fas fa-plus-circle text-xs"></i> สร้างอาชญากร
        </button>
      </div>
    </header>

    <!-- Filters -->
    <section class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">ตัวกรองอาชญากร</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Has Room Filter -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">สถานะห้อง</label>
            <select 
              v-model="filterHasRoom" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกสถานะห้อง"
            >
              <option value="">ทั้งหมด</option>
              <option :value="true">มีห้อง</option>
              <option :value="false">ไม่มีห้อง</option>
            </select>
          </div>
          <!-- Room Filter (Only if hasRoom is true) -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">เลือกจำนวนห้อง</label>
            <select 
              v-model.number="filterRoom" 
              @change="applyFilters" 
              :disabled="filterHasRoom === false"
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200 disabled:opacity-50"
              aria-label="เลือกจำนวนห้อง"
            >
              <option value="">เลือกจำนวนห้อง</option>
              <option v-for="n in 4" :key="n + 1" :value="n + 1">{{ n + 1 }}</option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto">
      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- Prompt to Select Filters -->
      <div v-if="filterHasRoom === '' && filterRoom === ''" class="text-center text-indigo-200 py-6">
        <i class="fas fa-door-closed text-2xl mb-2"></i>
        <p class="text-sm">กรุณาเลือกสถานะห้องหรือจำนวนห้องเพื่อแสดงรายการ</p>
      </div>

      <div v-else>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            v-for="criminal in displayedCriminals" 
            :key="criminal.id" 
            @click="openDetailModal(criminal)"
            class="bg-gray-900 rounded-md p-4 border border-gray-800 bg-opacity-80 hover:bg-opacity-80 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer shadow-xl"
          >
            <div class="flex items-center space-x-3 mb-3">
              <img 
                :src="criminal.imageUrl || 'default-criminal.png'" 
                alt="Criminal Image" 
                class="w-16 h-16 object-cover rounded-md border-2"
                :class="criminal.rarityColors?.borderColor || 'border-indigo-400'"
                @error="handleImageError"
              />
              <div>
                <h3 
                  class="text-sm font-semibold"
                  :class="criminal.rarityColors?.textColor || 'text-indigo-200'"
                >
                  {{ criminal.name }}
                </h3>
                <p class="text-xs text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-door-closed text-indigo-400"></i> 
                  {{ criminal.hasRoom ? `จำนวนห้อง ${criminal.room}` : 'ไม่มีห้อง' }}
                </p>
                <p class="text-xs text-indigo-200">เลเวล {{ criminal.level }}</p>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2 text-xs border-t border-gray-600 pt-3 mb-3">
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-heart text-red-400"></i> Sta: {{ criminal.stats?.stamina || 0 }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-dumbbell text-yellow-400"></i> Str: {{ criminal.stats?.str || 0 }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-brain text-purple-400"></i> Int: {{ criminal.stats?.int || 0 }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-running text-green-400"></i> Agi: {{ criminal.stats?.agi || 0 }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-shield-alt text-blue-400"></i> Def: {{ criminal.stats?.defense || 0 }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-clover text-lime-400"></i> Luck: {{ criminal.stats?.luck || 0 }}
              </p>
            </div>
            <div class="border-t border-gray-600 pt-3">
              <p class="text-xs text-indigo-200 text-center mb-2">รางวัล</p>
              <div v-if="criminal.renownReward > 0 || criminal.silverReward > 0 || criminal.goldReward > 0 || criminal.diamondReward > 0" class="grid grid-cols-2 gap-2 text-xs">
                <p v-if="criminal.renownReward > 0" class="text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-trophy text-indigo-400"></i> ชื่อเสียง: {{ criminal.renownReward }}
                </p>
                <p v-if="criminal.silverReward > 0" class="text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-coins text-gray-100"></i> เหรียญ: {{ criminal.silverReward }}
                </p>
                <p v-if="criminal.goldReward > 0" class="text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-g text-yellow-500"></i> ทอง: {{ criminal.goldReward }}
                </p>
                <p v-if="criminal.diamondReward > 0" class="text-indigo-200 flex items-center gap-1">
                  <i class="fas fa-gem text-blue-400"></i> เพชร: {{ criminal.diamondReward }}
                </p>
              </div>
              <p v-else class="text-xs text-indigo-200 text-center">ไม่มีรางวัล</p>
            </div>
            <div v-if="isAdmin" class="mt-3">
              <button 
                @click.stop="openEditModal(criminal.id)" 
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="แก้ไขอาชญากร"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
          </div>
        </div>

        <!-- No Criminals Message -->
        <div v-if="displayedCriminals.length === 0 && !isLoading && (filterHasRoom !== '' || filterRoom)" class="text-center text-indigo-200 py-6">
          <i class="fas fa-door-closed text-2xl mb-2"></i>
          <p class="text-sm">ไม่พบอาชญากรที่ตรงกับการค้นหา</p>
        </div>

        <!-- Load More Button -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMore && !isLoading" 
            @click="loadMore" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดเพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMore && displayedCriminals.length > 0" class="text-indigo-200 text-sm">ไม่มีอาชญากรเพิ่มเติมแล้ว</span>
        </div>
      </div>
    </main>

    <!-- Modals -->
    <AddCriminalModal 
      v-if="toggleModal === 'AddCriminalModal'" 
      @close="toggleModal = ''" 
      @criminalAdded="resetAndFetch"
    />
    <EditCriminalModal 
      v-if="toggleModal === 'EditCriminalModal'" 
      :criminalId="selectedCriminalId" 
      @close="toggleModal = ''" 
      @criminalUpdated="resetAndFetch"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import AddCriminalModal from '@/components/admin/AddCriminalModal.vue';
import EditCriminalModal from '@/components/admin/EditCriminalModal.vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  components: { AddCriminalModal, EditCriminalModal },
  setup() {
    const toggleModal = ref('');
    const criminals = ref([]);
    const filterRoom = ref('');
    const filterHasRoom = ref(''); // New filter for hasRoom
    const selectedCriminalId = ref(null); // Store criminal ID instead of full object
    const lastVisible = ref(null);
    const hasMore = ref(true);
    const isLoading = ref(false);
    const isAdmin = ref(false);
    const error = ref('');
    const criminalsPerLoad = 20;
    const currentPage = ref(1);

    const { user } = getUser();

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'default-criminal.png';
    };

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      } else {
        isAdmin.value = false;
      }
    }, { immediate: true });

    // Fetch criminals from Firestore
    const fetchCriminals = async (isLoadMore = false, reset = false) => {
      try {
        isLoading.value = true;
        error.value = '';
        let query = projectFirestore.collection('gameCriminals')
          .orderBy('level', 'desc')
          .orderBy('createdAt', 'desc')
          .limit(criminalsPerLoad);

        // Apply hasRoom filter
        if (filterHasRoom.value !== '') {
          query = query.where('hasRoom', '==', filterHasRoom.value);
        }

        // Apply room filter only if hasRoom is true or not specified
        if (filterRoom.value && filterHasRoom.value !== false) {
          query = query.where('room', '==', Number(filterRoom.value));
        }

        if (isLoadMore && lastVisible.value) {
          query = query.startAfter(lastVisible.value);
        }

        const snapshot = await query.get();

        if (snapshot.empty) {
          hasMore.value = false;
          if (reset || !isLoadMore) criminals.value = [];
          isLoading.value = false;
          return;
        }

        const newCriminals = snapshot.docs.map(doc => {
          const data = doc.data();
          if (!data.rarityColors) {
            data.rarityColors = {
              bgColor: 'bg-gray-700',
              textColor: 'text-white',
              borderColor: 'border-gray-600',
            };
          }
          return { id: doc.id, ...data };
        });

        lastVisible.value = snapshot.docs[snapshot.docs.length - 1];
        hasMore.value = snapshot.docs.length === criminalsPerLoad;

        if (isLoadMore && !reset) {
          criminals.value = [...criminals.value, ...newCriminals];
        } else {
          criminals.value = newCriminals;
          currentPage.value = 1;
        }
      } catch (err) {
        console.error('Error fetching criminals:', err);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลอาชญากร: ' + err.message;
        if (err.code === 'failed-precondition') {
          error.value += ' กรุณาสร้าง composite index ใน Firestore console';
        }
      } finally {
        isLoading.value = false;
      }
    };

    // Compute displayed criminals for pagination
    const displayedCriminals = computed(() => {
      const start = (currentPage.value - 1) * criminalsPerLoad;
      const end = start + criminalsPerLoad;
      return criminals.value.slice(start, end);
    });

    // Apply filters and reset
    const applyFilters = () => {
      if (filterHasRoom.value === false) {
        filterRoom.value = ''; // Reset room filter when hasRoom is false
      }
      criminals.value = [];
      lastVisible.value = null;
      hasMore.value = true;
      fetchCriminals(false, true);
    };

    // Clear filters
    const clearFilters = () => {
      filterHasRoom.value = '';
      filterRoom.value = '';
      applyFilters();
    };

    // Load more criminals
    const loadMore = () => {
      if (currentPage.value * criminalsPerLoad >= criminals.value.length && hasMore.value) {
        fetchCriminals(true);
      }
      currentPage.value += 1;
    };

    // Reset and fetch criminals
    const resetAndFetch = () => {
      criminals.value = [];
      lastVisible.value = null;
      hasMore.value = true;
      currentPage.value = 1;
      fetchCriminals();
    };

    // Open detail modal
    const openDetailModal = (criminal) => {
      toggleModal.value = 'DetailCriminalModal';
    };

    // Open edit modal with ID
    const openEditModal = (criminalId) => {
      selectedCriminalId.value = criminalId;
      toggleModal.value = 'EditCriminalModal';
    };

    onMounted(() => {
      // admin status is now reactive via store watch
    });

    return { 
      toggleModal, 
      filterHasRoom,
      filterRoom, 
      criminals, 
      displayedCriminals, 
      selectedCriminalId, 
      openDetailModal, 
      openEditModal, 
      fetchCriminals, 
      loadMore, 
      hasMore, 
      isLoading, 
      isAdmin, 
      resetAndFetch, 
      applyFilters, 
      clearFilters, 
      error, 
      handleImageError 
    };
  },
};
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:disabled, input:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fcondo.png?alt=media&token=198ebb1c-66e5-4bf0-aa79-32bff287aa09') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}
</style>